import CloseIcon from '@mui/icons-material/Close'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import {
  Box,
  ClickAwayListener,
  IconButton,
  Paper,
  Popper,
  Typography
} from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'

interface WizardPointerProps {
  target: string // CSS selector or ref
  content: string
  placement?: 'top' | 'bottom' | 'left' | 'right'
  visible: boolean
  onDismiss: () => void
}

/**
 * WizardPointer - Reusable tooltip/pointer component for visual guidance
 *
 * Provides contextual hints positioned relative to target elements.
 * Features:
 * - Positioned using Material-UI Popper
 * - Dismissible by user
 * - Auto-hides after 10 seconds
 * - Animated entrance
 * - Accessible with ARIA labels
 *
 * Requirements: 8.1, 8.2, 8.3, 8.4, 8.5
 */
const WizardPointer: React.FC<WizardPointerProps> = ({
  target,
  content,
  placement = 'bottom',
  visible,
  onDismiss
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const autoHideTimerRef = useRef<NodeJS.Timeout | null>(null)

  // Find target element and set as anchor
  useEffect(() => {
    if (visible) {
      const targetElement = document.querySelector(target) as HTMLElement
      if (targetElement) {
        setAnchorEl(targetElement)
      } else {
        console.warn(`WizardPointer: Target element "${target}" not found`)
      }
    } else {
      setAnchorEl(null)
    }
  }, [target, visible])

  // Auto-hide after 10 seconds
  useEffect(() => {
    if (visible && anchorEl) {
      autoHideTimerRef.current = setTimeout(() => {
        onDismiss()
      }, 10000)

      return () => {
        if (autoHideTimerRef.current) {
          clearTimeout(autoHideTimerRef.current)
        }
      }
    }
    return undefined
  }, [visible, anchorEl, onDismiss])

  // Handle click away
  const handleClickAway = () => {
    onDismiss()
  }

  if (!visible || !anchorEl) {
    return null
  }

  return (
    <Popper
      open={visible}
      anchorEl={anchorEl}
      placement={placement}
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 8]
          }
        },
        {
          name: 'preventOverflow',
          options: {
            padding: 8
          }
        }
      ]}
      style={{ zIndex: 1300 }}
    >
      <ClickAwayListener onClickAway={handleClickAway}>
        <Paper
          elevation={8}
          sx={{
            maxWidth: 320,
            p: 2,
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            border: 'none',
            animation: 'fadeIn 0.3s ease-in-out',
            '@keyframes fadeIn': {
              from: {
                opacity: 0,
                transform: 'translateY(-8px)'
              },
              to: {
                opacity: 1,
                transform: 'translateY(0)'
              }
            }
          }}
          role='tooltip'
          aria-live='polite'
          aria-label={content}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            {/* Info icon */}
            <InfoOutlinedIcon
              sx={{
                fontSize: 20,
                mt: 0.25,
                flexShrink: 0
              }}
            />

            {/* Content */}
            <Typography
              variant='body2'
              sx={{
                flex: 1,
                fontSize: '0.875rem',
                lineHeight: 1.5
              }}
            >
              {content}
            </Typography>

            {/* Dismiss button */}
            <IconButton
              size='small'
              onClick={onDismiss}
              sx={{
                color: 'primary.contrastText',
                padding: 0.5,
                marginTop: -0.5,
                marginRight: -0.5,
                flexShrink: 0,
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
              aria-label='Dismiss hint'
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Paper>
      </ClickAwayListener>
    </Popper>
  )
}

export default WizardPointer
